'use client';

import { useCallback, useState } from 'react';

import { useCurrentUserId } from '@ps/ui/contexts/auth';

import { useCommunityContext } from '../contexts/community/useCommunityContext';
import { leaveChannel } from '../functions/members/leaveChannel';
import type { CommunityChannel } from '../types/CommunityChannel';

/**
 * Leave action for the current member + a channel — the web port of PopshopRN's
 * `useLeaveCommunityChannel`. POSTs the remove-member call and refreshes the
 * channel list; the live membership doc subscription (`useMyChannelMembership`)
 * then flips the viewer back to the non-member gate. Returns a
 * `{ leave, leaving, error }` tuple for the Leave CTA.
 */
export function useLeaveChannel(channel: CommunityChannel | null | undefined) {
  const { creatorId, refreshChannels } = useCommunityContext();
  const currentUserId = useCurrentUserId();
  const [leaving, setLeaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const leave = useCallback(async () => {
    // Lazy-auth: `undefined` = resolving. Acting now could misroute a viewer
    // whose session is about to restore; `null` (anonymous) can't be a member.
    if (!currentUserId || !creatorId || !channel) return;

    setLeaving(true);
    setError(null);
    try {
      await leaveChannel({
        creatorId,
        channelId: channel.id,
        userId: currentUserId,
      });
      await refreshChannels();
    } catch (cause: unknown) {
      const leaveError =
        cause instanceof Error ? cause : new Error(String(cause));
      console.error('Failed to leave community channel:', leaveError);
      setError(leaveError);
    } finally {
      setLeaving(false);
    }
  }, [creatorId, currentUserId, channel, refreshChannels]);

  return { leave, leaving, error };
}
